import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryQuickEntry from '@/pages/home/components/CategoryQuickEntry';
import RankingSidebar from '@/pages/home/components/RankingSidebar';
import SectionHeader from '@/components/base/SectionHeader';
import VideoCard from '@/components/feature/VideoCard';
import { EmptyState, LoadingState } from '@/components/base/StateBlocks';
import { useSimulatedLoad } from '@/hooks/useSimulatedLoad';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getHotVideos, getLatestVideos } from '@/utils/videos';

export default function Home() {
  useDocumentMeta({
    title: '红桃视频 - 高清在线观看视频平台',
    description:
      '红桃视频为您提供丰富的高清视频内容，涵盖电影、电视剧、综艺等多类别，方便用户在线免费观看。畅享多样视听盛宴，提升观影体验。',
    keywords: '红桃视频,高清视频,在线电影,电视剧在线观看,免费在线影院,在线视频播放',
  });

  const { loading } = useSimulatedLoad(420);
  const latest = getLatestVideos(8);
  const hot = getHotVideos(8);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <HeroCarousel />

      <div className="mt-8">
        <CategoryQuickEntry />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
        <div className="min-w-0">
          <section>
            <SectionHeader
              title="最新上线"
              subtitle="按更新时间排序，最新影视综内容抢先看"
              icon="ri-time-line"
              moreTo="/category/movie"
            />
            {loading ? (
              <LoadingState text="正在加载最新内容…" />
            ) : latest.length === 0 ? (
              <EmptyState title="暂无推荐内容" description="稍后再来看看吧" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                {latest.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            )}
          </section>

          <section className="mt-12">
            <SectionHeader
              title="热门推荐"
              subtitle="依据播放量排行，大家都在看的高分内容"
              icon="ri-fire-line"
              moreTo="/category/tv"
            />
            {loading ? (
              <LoadingState text="正在加载热门内容…" />
            ) : hot.length === 0 ? (
              <EmptyState title="暂无推荐内容" description="稍后再来看看吧" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                {hot.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            )}
          </section>
        </div>

        <RankingSidebar />
      </div>

      <section className="mt-14 rounded-xl bg-background-100 border border-background-200/70 p-6 md:p-10">
        <div className="max-w-3xl">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
            关于红桃视频
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
            红桃视频致力于成为你的高清观影伙伴。我们汇集电影、电视剧、综艺等多元内容，用清晰的分类、便捷的搜索和用心的推荐，帮你更快找到想看的作品。无论是一个人的深夜，还是一群人的周末，都能在这里找到合适的那一部。
          </p>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '24+', label: '精选影视内容', icon: 'ri-film-line' },
            { value: '3', label: '核心内容分类', icon: 'ri-apps-2-line' },
            { value: '高清', label: '多清晰度体验', icon: 'ri-hd-line' },
            { value: '每日', label: '内容持续更新', icon: 'ri-refresh-line' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg bg-background-200/50 p-4">
              <span className="w-9 h-9 rounded-md bg-primary-500/12 flex items-center justify-center text-primary-400">
                <i className={`${stat.icon} text-lg`} />
              </span>
              <p className="mt-3 font-heading text-xl font-bold text-foreground-950">{stat.value}</p>
              <p className="text-xs text-foreground-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            了解我们
            <i className="ri-arrow-right-line text-base" />
          </Link>
        </div>
      </section>
    </div>
  );
}